import { useMutation } from '@apollo/client';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { kml as toGeoJSON } from '@tmcw/togeojson';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { graphql } from '~/apollo/generated/v4';
import type { LatLngInput } from '~/apollo/generated/v4/graphql';
import { Alert } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';
import { CollapsibleCard, useCollapseControl } from '~/components/ui/collapse';
import { FormLabel } from '~/components/ui/forms/form-label';
import { KmlFileUpload } from '~/components/upload/georeference-v4/kml-import/kml-file-upload';
import type { RefetchQueries } from '~/utils/graphql';

const SAVE_CLIPPING_OUTLINE = graphql(`
  mutation SaveClippingOutline(
    $id: ID!
    $input: CesiumAssetSaveClippingOutlineInput!
  ) {
    cesiumAssetSaveClippingOutline(id: $id, input: $input) {
      result {
        id
      }
    }
  }
`);

// Simplified schema that is the expected result of the KML->toGeoJSON conversion
const featureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(
    z.object({
      type: z.literal('Feature'),
      geometry: z.object({
        type: z.literal('Polygon'),
        coordinates: z
          .array(
            z.array(
              z
                .tuple([z.number(), z.number(), z.number()])
                .transform(([lng, lat, _]) => ({ lat, lng })),
            ),
          )
          // The KML/GeoJSON spec allows for multiple polygons in a single feature, but we only want the first one
          .transform(([firstPolygon]) => firstPolygon),
      }),
    }),
  ),
});

function parseKmlFile(fileContents: string) {
  try {
    const kml = new DOMParser().parseFromString(fileContents, 'text/xml');
    const geoJSON = toGeoJSON(kml);
    const parsed = featureCollectionSchema.parse(geoJSON);
    return parsed.features.map(feature => feature.geometry.coordinates);
  } catch (error) {
    console.error('Error parsing KML file:', error);
    return null;
  }
}

export function TerrainClippingPolygonForm({
  cesiumAssetId,
  refetchQueries,
  onClippingOutlineUpdated,
  clippingPolygon,
}: {
  cesiumAssetId: number;
  refetchQueries: RefetchQueries;
  onClippingOutlineUpdated: (coordinates: LatLngInput[] | null) => void;
  clippingPolygon: LatLngInput[] | null | undefined;
}) {
  const collapseControl = useCollapseControl();

  const [coordinateSets, setCoordinateSets] = useState<LatLngInput[][]>([]);
  const [coordinateSetIdx, setCoordinateSetIdx] = useState<number | null>(null);

  const [saveClippingOutline, { loading }] = useMutation(
    SAVE_CLIPPING_OUTLINE,
    { refetchQueries },
  );

  function handleParseFileSuccess(fileContents: string) {
    const parsedCoordinateSets = parseKmlFile(fileContents);

    if (parsedCoordinateSets?.length) {
      setCoordinateSets(parsedCoordinateSets);
      setCoordinateSetIdx(0);
      handleCoordinatesUpdated(parsedCoordinateSets.at(0) ?? null);
      toast.success('Clipping outline loaded.');
    } else {
      toast.error('KML file could not be parsed or no polygons were found.');
      setCoordinateSets([]);
      setCoordinateSetIdx(null);
      handleCoordinatesUpdated(null);
    }
  }

  function handleCoordinatesUpdated(coordinates: LatLngInput[] | null) {
    onClippingOutlineUpdated(coordinates);
  }

  async function handleSave() {
    const coordinates = coordinateSets.at(coordinateSetIdx ?? -1);

    if (!coordinates) {
      return void toast.error('No coordinates to save.');
    }

    try {
      await saveClippingOutline({
        variables: { id: cesiumAssetId, input: { coords: coordinates } },
      });
      toast.success('Clipping outline saved.');
      collapseControl.setIsOpen(false);
    } catch (error) {
      console.error('Error saving clipping outline:', error);
      toast.error('There was a problem saving the clipping outline.');
    }
  }

  function handleIndexChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const index = parseInt(e.target.value);
    setCoordinateSetIdx(index);
    handleCoordinatesUpdated(coordinateSets.at(index) ?? null);
  }

  const isOutlineSet =
    typeof coordinateSets.at(coordinateSetIdx ?? -1) !== 'undefined';

  return (
    <CollapsibleCard title="Terrain Clipping Polygon" {...collapseControl}>
      <div className="space-y-4">
        {clippingPolygon && (
          <Alert status="success">
            <FontAwesomeIcon icon={faCheckCircle} />
            <div>A clipping outline is currently set.</div>
          </Alert>
        )}

        <KmlFileUpload onUploadSuccess={handleParseFileSuccess} />

        {coordinateSets.length > 0 && (
          <div>
            <FormLabel label="Coordinate Set" required />
            <select
              name="coordinateSet"
              value={coordinateSetIdx ?? ''}
              onChange={handleIndexChange}
              className="w-full select"
            >
              {coordinateSets.map((_, index) => (
                <option key={index} value={index.toString()}>
                  Polygon {index + 1}
                </option>
              ))}
            </select>
          </div>
        )}

        {coordinateSets.length > 0 && coordinateSetIdx !== null && (
          <div className="card-actions justify-center">
            <Button
              type="button"
              onClick={handleSave}
              color="primary"
              loading={loading}
              disabled={!isOutlineSet}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    </CollapsibleCard>
  );
}
